import React from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  Button,
  Checkbox,
  Container,
  Divider,
  Form,
  Grid,
  Icon,
  Label,
  Message,
  SemanticICONS,
  Table,
  TextArea,
} from "semantic-ui-react";
import styled from "styled-components";

import { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";

import { useAppSelector } from "store";
import constants from "utils/constants";
import util from "utils/utils";

import { ImageChooser, VideoChooser } from "components/lib/Choosers";
import SettingActions from "components/lib/SettingActions";

const StyledAvatar = styled.div<{ $image: string }>`
  background-size: cover;
  background-position: center center;
  background-image: url(${({ $image }) => $image});
  height: 200px;
  width: 200px;
  display: block;
  margin: 10px auto 40px auto;
  border: 5px solid white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
  border-radius: 50%;
`;

const StyledDefaultAvatar = styled.div`
  display: inline-block;
  img {
    cursor: pointer;
    opacity: 0.8;
    transition: 0.3s opacity;
    width: 40px;
    margin-right: 10px;
    margin-bottom: 5px;
    border-radius: 50%;
    box-shadow: 2px 2px 4px rgba(50, 50, 50, 0.4);
    &:hover {
      opacity: 1;
    }
  }
`;

const StyledCover = styled.div<{ $image: string }>`
  height: 200px;
  width: 100%;
  background-size: cover;
  background-position: center center;
  background-image: url(${({ $image }) => $image});
  margin: 10px auto 40px auto;
  border: 5px solid white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
`;

type UserSettingsProfileProps = {
  updateProfile: (key: string, value: any) => void;
  toggleTag: (tag: OpenAPI.Schemas["Tag"], checked: boolean) => void;
  saveProfile: () => void;
  saving: boolean;
};

const UserSettingsProfile = ({
  updateProfile,
  toggleTag,
  saveProfile,
  saving,
}: UserSettingsProfileProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const removeUpload = (i) => {
    const uploads = Object.assign([], user.profile.uploads);
    uploads.splice(i, 1);
    updateProfile("uploads", uploads);
  };

  if (!user.profile) return null;
  return (
    <Container>
      <h3>{t("users.edit.name.title")}</h3>
      {user?.ssoUser ?
        <Message>
          Your avatar was automatically set from your single sign-on account.
        </Message>
      : null}
      <Form>
        <Form.Group>
          <Form.Field
            control={Input}
            disabled={user?.ssoUser}
            label={t("users.edit.name.firstName")}
            defaultValue={user.profile.firstName}
            placeholder={t("users.edit.name.firstName")}
            onChange={(e) => updateProfile("firstName", e.target.value)}
            autoComplete="given-name"
          />
          <Form.Field
            control={Input}
            disabled={user?.ssoUser}
            label={t("users.edit.name.lastName")}
            defaultValue={user.profile.lastName}
            placeholder={t("users.edit.name.lastName")}
            onChange={(e) => updateProfile("lastName", e.target.value)}
            autoComplete="family-name"
          />
        </Form.Group>
      </Form>
      <Divider section />

      <h3>{t("users.edit.avatar.title")}</h3>
      <p>
        {user.profile.avatarWasSetByExternalSource ?
          <Message>
            Your avatar was automatically set from your single sign-on account.
          </Message>
        : t("users.edit.avatar.info")}
      </p>
      <div>
        <StyledAvatar
          $image={util.mixinCssUrlFallback(
            util.avatarUrl(user, "500x500"),
            util.avatarUrl(),
          )}
        />
        {!user.profile.avatarWasSetByExternalSource ?
          <>
            <ImageChooser
              forType="user"
              forId={user._id}
              trigger={
                <Button
                  secondary
                  content={t("users.edit.avatar.chooseImage")}
                  icon="image"
                />
              }
              onComplete={(f, _n, u) => {
                updateProfile("avatar", f);
                updateProfile("avatarUrl", u);
                updateProfile("avatarThumbnailUrls", {});
              }}
            />
            <p style={{ marginTop: 15 }}>{t("users.edit.avatar.default")}</p>
            <div>
              {constants.avatars.map((avatar, i) => (
                <StyledDefaultAvatar
                  key={i}
                  onClick={() => {
                    updateProfile("avatar", avatar);
                    updateProfile(
                      "avatarUrl",
                      `https://cdn.simplydo.co.uk/images/avatars/${avatar}`,
                    );
                    updateProfile("avatarThumbnailUrls", {});
                  }}
                >
                  <img
                    alt="Default avatar"
                    src={`https://cdn.simplydo.co.uk/images/avatars/${avatar}`}
                  />
                </StyledDefaultAvatar>
              ))}
            </div>
          </>
        : null}
      </div>
      <Divider />

      <h3>{t("users.edit.coverImage.title")}</h3>
      <p>{t("users.edit.coverImage.choose")}</p>
      <div>
        {user.profile.coverImageUrl && (
          <StyledCover
            $image={util.getThumbnail(user.profile, "coverImage", "300x300")}
          />
        )}
        <ImageChooser
          forType="user"
          forId={user._id}
          trigger={
            <Button
              secondary
              content={t("users.edit.coverImage.select")}
              icon="image"
            />
          }
          onComplete={(f, _n, u) => {
            updateProfile("coverImage", f);
            updateProfile("coverImageUrl", u);
            updateProfile("coverImageThumbnailUrls", {});
          }}
        />
        {user.profile.coverImageUrl && (
          <Button
            basic
            icon="trash"
            size="small"
            onClick={() => {
              updateProfile("coverImage", null);
              updateProfile("coverImageUrl", null);
              updateProfile("coverImageThumbnailUrls", null);
            }}
            style={{ marginLeft: 10 }}
          />
        )}
      </div>

      <Divider section />
      <h3>{t("users.edit.video.title")}</h3>
      <p>{t("users.edit.video.info")}</p>
      {user.profile.videoUrl && <VideoPlayer src={user.profile.videoUrl} />}
      <div style={{ marginTop: 20 }} />
      <div style={{ display: "flex" }}>
        <VideoChooser
          forType="user"
          forId={user._id}
          onComplete={(v, _n, u) => {
            updateProfile("video", v);
            updateProfile("videoUrl", u);
          }}
          onError={toast.error}
          trigger={
            <Button
              secondary
              size="small"
              content={t("users.edit.video.chooseVideo")}
              icon="video"
            />
          }
        />
        {user.profile.video ?
          <Button
            basic
            icon="trash"
            size="small"
            onClick={() => {
              updateProfile("video", "");
              updateProfile("videoUrl", null);
            }}
            style={{ marginLeft: 10 }}
          />
        : null}
      </div>

      <Divider />
      <h3>{t("generic.overview")}</h3>
      <p>{t("users.edit.overview.info")}</p>
      <p>
        <b>{t("users.edit.overview.tagline.title")}</b>
      </p>

      <Form>
        <Form.Field
          control={Input}
          defaultValue={user.profile.tagline}
          placeholder={t("users.edit.overview.tagline.placeholder")}
          onChange={(e) => updateProfile("tagline", e.target.value)}
        />
      </Form>
      <Divider hidden />
      <p>
        <b>{t("users.edit.overview.professionalProfile.title")}</b>
      </p>
      <Form>
        <TextArea
          defaultValue={user.profile.overview}
          style={{ minHeight: 200 }}
          onChange={(e) => updateProfile("overview", e.target.value)}
        />
      </Form>

      <Divider />
      <Grid>
        <Grid.Column width={8}>
          <h3>{t("users.edit.overview.professionalPortfolio.title")}</h3>
          <Form>
            <Form.Field>
              <Form.Field
                control={Input}
                icon="linkedin"
                iconPosition="left"
                defaultValue={user.profile.linkedin}
                placeholder={t(
                  "users.edit.overview.professionalPortfolio.linkedIn",
                )}
                onChange={(e) => updateProfile("linkedin", e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <Form.Field
                control={Input}
                icon="github"
                iconPosition="left"
                defaultValue={user.profile.github}
                placeholder={t(
                  "users.edit.overview.professionalPortfolio.github",
                )}
                onChange={(e) => updateProfile("github", e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <Form.Field
                control={Input}
                icon="instagram"
                iconPosition="left"
                defaultValue={user.profile.instagram}
                placeholder={t(
                  "users.edit.overview.professionalPortfolio.instagram",
                )}
                onChange={(e) => updateProfile("instagram", e.target.value)}
              />
            </Form.Field>
          </Form>
        </Grid.Column>
        <Grid.Column width={8}>
          <h3>{t("users.edit.overview.social.title")}</h3>
          <Form>
            <Form.Field>
              <Form.Field
                control={Input}
                icon="mouse pointer"
                iconPosition="left"
                defaultValue={user.profile.website}
                placeholder="https://www.example.com"
                onChange={(e) => updateProfile("website", e.target.value)}
              />
            </Form.Field>
            <Form.Field>
              <Form.Field
                control={Input}
                labelPosition="left"
                defaultValue={user.profile.twitter}
                placeholder={t("users.edit.overview.social.twitter")}
                onChange={(e) => updateProfile("twitter", e.target.value)}
              >
                <Label
                  basic
                  style={{ borderRight: "0px", paddingLeft: 14, color: "gray" }}
                >
                  𝕏
                </Label>
                <input style={{ paddingLeft: 2 }} />
              </Form.Field>
            </Form.Field>
            <Form.Field>
              <Form.Field
                control={Input}
                icon="facebook"
                iconPosition="left"
                defaultValue={user.profile.facebook}
                placeholder={t("users.edit.overview.social.facebook")}
                onChange={(e) => updateProfile("facebook", e.target.value)}
              />
            </Form.Field>
          </Form>
        </Grid.Column>
      </Grid>
      <span
        style={{
          display: "block",
          opacity: 0.6,
          fontStyle: "italic",
          marginTop: 10,
          fontSize: "0.85em",
        }}
      >
        For online accounts, please only include your account id, not the entire
        URL. For example, rather than{" "}
        <b>&quot;https://facebook.com/brianmcbrain&quot;</b>, just use{" "}
        <b>&quot;brianmcbrain&quot;</b>.
      </span>

      {user.profile.uploads && user.profile.uploads.length > 0 && (
        <React.Fragment>
          <Divider hidden />
          <h3>{t("users.edit.files.uploads")}</h3>
          <Table style={{ marginTop: 0 }} fixed basic="very">
            <Table.Body>
              {user.profile.uploads.map((a, i) => (
                <Table.Row key={a.storedName}>
                  <Table.Cell>
                    <Icon
                      name={util.getFileIcon(a.storedName) as SemanticICONS}
                    />{" "}
                    {a.title}
                  </Table.Cell>
                  <Table.Cell>
                    <a target="_blank" rel="noreferrer noopener" href={a.url}>
                      {a.name}
                    </a>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button
                      size="small"
                      basic
                      icon="trash"
                      onClick={() => removeUpload(i)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </React.Fragment>
      )}

      {user.ownerOrganisation.tagList?.filter(
        (tag) => !tag?.disabledEntities?.includes("users"),
      )?.length > 0 && (
        <>
          <Divider section />
          <h3>{t("tags.title")}</h3>
          <p>{t("tags.selectTags")}</p>
          <Grid>
            {user.ownerOrganisation.tagList
              ?.filter((tag) => !tag?.disabledEntities?.includes("users"))
              .map((tag) => (
                <Grid.Column computer={5} mobile={8} key={tag.value}>
                  <Checkbox
                    toggle
                    label={tag.value}
                    checked={(user.tags || []).indexOf(tag._id) > -1}
                    onChange={(_e, c) => toggleTag(tag, c.checked as boolean)}
                  />
                </Grid.Column>
              ))}
          </Grid>
        </>
      )}

      <Divider section hidden />
      <SettingActions>
        <Button
          primary
          icon="check"
          content={t("generic.saveChanges")}
          onClick={saveProfile}
          loading={saving}
        />
      </SettingActions>
    </Container>
  );
};

export default UserSettingsProfile;
